import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarDays, Check, CheckCircle2, Clock3, Coffee, Croissant, FilePlus2, Fingerprint,
  Loader2, Lock, LogIn, LogOut, Paperclip, Power, QrCode, Store, UtensilsCrossed,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fmtApiError } from "@/lib/api";
import Teclado from "@/components/kiosco/Teclado";
import Camara from "@/components/kiosco/Camara";
import { useAhoraBA } from "@/components/ClockBA";

const TZ = "America/Argentina/Buenos_Aires";
const MIN_PAUSA = { desayuno: 10, almuerzo: 30, merienda: 10 };

const hoyISO = () => new Date().toLocaleDateString("en-CA", { timeZone: TZ });

const fechaLarga = (d) => {
  const s = new Intl.DateTimeFormat("es-AR", {
    timeZone: TZ, weekday: "long", day: "numeric", month: "long", year: "numeric",
  }).format(d);
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const hhmm = (d) => new Intl.DateTimeFormat("es-AR", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
const ss = (d) => new Intl.DateTimeFormat("es-AR", { timeZone: TZ, second: "2-digit" }).format(d);

const TIPOS_JUST = [
  { id: "turno_medico", label: "Turno médico" },
  { id: "tardanza", label: "Tardanza" },
  { id: "falta_dia_entero", label: "Falta de día entero" },
  { id: "retiro_anticipado", label: "Retiro anticipado" },
  { id: "otro", label: "Otro" },
];

const PAUSAS = [
  { tipo: "desayuno", label: "Desayuno", sub: "Salir a desayunar", icon: Coffee },
  { tipo: "almuerzo", label: "Almuerzo", sub: "Salir a almorzar", icon: UtensilsCrossed },
  { tipo: "merienda", label: "Merienda", sub: "Salir a merendar", icon: Croissant },
];

function Campo({ etiqueta, valor, puntos, activo, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full rounded-2xl border bg-[#0B0F14] px-4 py-3 text-left transition-colors ${
        activo ? "border-[#2DD4BF]" : "border-white/10 hover:border-white/25"
      }`}
    >
      <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-gray-500">{etiqueta}</span>
      <div className="flex gap-2">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`flex h-9 w-8 items-center justify-center rounded-lg text-xl font-bold tabular-nums ${
              valor[i] ? "bg-[#2DD4BF]/15 text-[#2DD4BF]" : "bg-white/5 text-gray-600"
            }`}
          >
            {valor[i] ? (puntos ? "•" : valor[i]) : puntos ? "•" : "–"}
          </span>
        ))}
      </div>
    </button>
  );
}

function Tarjeta({ icon: Icon, titulo, subtitulo, minutos, estado, horaUsada, onClick, testid }) {
  const estilos = {
    sugerida: "border-[#2DD4BF] bg-[#2DD4BF]/10 cursor-pointer hover:bg-[#2DD4BF]/15",
    disponible: "border-white/10 bg-[#151A21] cursor-pointer hover:border-[#2DD4BF]/50",
    bloqueada: "border-white/5 bg-[#151A21]/50 opacity-40 cursor-not-allowed",
    usada: "border-white/5 bg-[#151A21]/60 opacity-60 cursor-default",
  };
  const clickeable = estado === "sugerida" || estado === "disponible";
  return (
    <button
      type="button"
      onClick={clickeable ? onClick : undefined}
      disabled={!clickeable}
      data-testid={testid}
      className={`relative flex h-28 flex-col items-start justify-center gap-1 rounded-2xl border p-5 text-left transition-all ${estilos[estado]} ${clickeable ? "active:scale-[0.98]" : ""}`}
    >
      {minutos != null && (
        <span className="absolute right-3 top-3 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-gray-300">
          {minutos} min
        </span>
      )}
      {estado === "bloqueada" && <Lock size={15} className="absolute right-3.5 top-3.5 text-gray-500" />}
      {estado === "usada" && <Check size={15} className="absolute right-3.5 top-3.5 text-[#2DD4BF]" />}
      <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${
        estado === "bloqueada" || estado === "usada" ? "bg-white/5 text-gray-500" : "bg-[#2DD4BF]/15 text-[#2DD4BF]"
      }`}>
        <Icon size={19} />
      </span>
      <span className="text-base font-bold text-white">{titulo}</span>
      <span className="text-xs text-gray-500">
        {estado === "usada" && horaUsada ? `Fichada ${horaUsada}` : subtitulo}
      </span>
    </button>
  );
}

export default function Kiosco() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const ahora = useAhoraBA();

  const [pantalla, setPantalla] = useState("reposo"); // reposo | acciones | repin | justificar | qr | confirmacion
  const [etapa, setEtapa] = useState("legajo");
  const [valor, setValor] = useState("");
  const [legajo, setLegajo] = useState("");
  const [error, setError] = useState("");
  const [empleado, setEmpleado] = useState(null);
  const [hoy, setHoy] = useState(null);
  const [accion, setAccion] = useState(null);
  const [pinAccion, setPinAccion] = useState("");
  const [camara, setCamara] = useState(null); // { modo: "fichada" | "comprobante" | "prueba" }
  const [confirm, setConfirm] = useState(null);
  const [just, setJust] = useState({ tipo: "turno_medico", fecha: hoyISO(), descripcion: "", archivo_url: null });
  const [qr, setQr] = useState(null);
  const [procesando, setProcesando] = useState(false);

  const volverReposo = useCallback(() => {
    setPantalla("reposo");
    setEtapa("legajo");
    setValor("");
    setLegajo("");
    setError("");
    setEmpleado(null);
    setHoy(null);
    setAccion(null);
    setPinAccion("");
    setCamara(null);
    setConfirm(null);
    setJust({ tipo: "turno_medico", fecha: hoyISO(), descripcion: "", archivo_url: null });
    setQr(null);
  }, []);

  const ultAct = useRef(Date.now());
  const [restante, setRestante] = useState(60);
  useEffect(() => {
    const marcar = () => { ultAct.current = Date.now(); };
    window.addEventListener("pointerdown", marcar);
    window.addEventListener("keydown", marcar);
    const t = setInterval(() => {
      if (!empleado) return;
      const r = 60 - Math.floor((Date.now() - ultAct.current) / 1000);
      setRestante(r);
      if (r <= 0) {
        volverReposo();
        toast.info("Sesión cerrada por inactividad");
      }
    }, 1000);
    return () => {
      window.removeEventListener("pointerdown", marcar);
      window.removeEventListener("keydown", marcar);
      clearInterval(t);
    };
  }, [empleado, volverReposo]);

  useEffect(() => {
    if (pantalla !== "confirmacion") return;
    const t = setTimeout(volverReposo, 3000);
    return () => clearTimeout(t);
  }, [pantalla, volverReposo]);

  useEffect(() => {
    if (!qr) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/kiosco/qr-estado/${qr.token}`);
        if (data.listo) {
          setJust((j) => ({ ...j, archivo_url: data.archivo_url }));
          setQr(null);
          setPantalla("justificar");
          toast.success("Comprobante recibido del celular");
        } else if (data.vencido) {
          setQr(null);
          setPantalla("justificar");
          toast.error("El código QR venció. Generá uno nuevo.");
        }
      } catch { /* reintenta en el próximo tick */ }
    }, 2000);
    return () => clearInterval(t);
  }, [qr]);

  const refrescarHoy = async (empId) => {
    const { data } = await api.get(`/kiosco/estado/${empId}`);
    setHoy(data);
  };

  const tecla = useCallback((t) => setValor((v) => (v.length < 4 ? v + t : v)), []);
  const borrar = useCallback(() => { setValor((v) => v.slice(0, -1)); setError(""); }, []);

  const enterReposo = useCallback(async () => {
    if (valor.length < 4) {
      setError(etapa === "legajo" ? "Ingresá los 4 dígitos del legajo" : "Ingresá los 4 dígitos del PIN");
      return;
    }
    setError("");
    if (etapa === "legajo") {
      setLegajo(valor);
      setEtapa("pin");
      setValor("");
      return;
    }
    setProcesando(true);
    try {
      const { data } = await api.post("/kiosco/identificar", { legajo, pin: valor });
      setEmpleado(data.empleado);
      setHoy(data.hoy);
      setPantalla("acciones");
      setValor("");
    } catch (e) {
      setError(fmtApiError(e));
      setValor("");
    } finally {
      setProcesando(false);
    }
  }, [valor, etapa, legajo]);

  const clickAccion = (tipo) => {
    setAccion(tipo);
    setValor("");
    setError("");
    setPantalla("repin");
  };

  const enterRepin = useCallback(async () => {
    if (valor.length < 4) { setError("Ingresá los 4 dígitos del PIN"); return; }
    setProcesando(true);
    setError("");
    try {
      await api.post("/kiosco/identificar", { legajo: empleado.legajo, pin: valor });
      setPinAccion(valor);
      setValor("");
      setCamara({ modo: "fichada" });
    } catch (e) {
      setError(fmtApiError(e));
      setValor("");
    } finally {
      setProcesando(false);
    }
  }, [valor, empleado]);

  // Teclado físico de la PC
  const accionesRef = useRef({});
  accionesRef.current = { tecla, borrar, enterReposo, enterRepin };
  useEffect(() => {
    if (pantalla !== "reposo" && pantalla !== "repin") return;
    const h = (e) => {
      if (/^[0-9]$/.test(e.key)) accionesRef.current.tecla(e.key);
      else if (e.key === "Backspace") accionesRef.current.borrar();
      else if (e.key === "Enter") (pantalla === "reposo" ? accionesRef.current.enterReposo() : accionesRef.current.enterRepin());
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [pantalla]);

  const subirFoto = async (dataUrl) => (await api.post("/kiosco/subir-foto", { imagen: dataUrl })).data.url;

  const onFoto = async (dataUrl) => {
    if (camara.modo === "prueba") {
      setCamara(null);
      toast.success("La cámara funciona bien");
      return;
    }
    setProcesando(true);
    try {
      const url = await subirFoto(dataUrl);
      if (camara.modo === "fichada") {
        const { data } = await api.post("/kiosco/fichar", {
          empleado_id: empleado.id, tipo: accion, pin: pinAccion, foto_url: url,
        });
        setCamara(null);
        setConfirm({
          titulo: `${accion === "entrada" ? "Entrada" : "Salida"} registrada ${data.hora}`,
          detalle: `Local ${user.local_nombre}`,
        });
        setPantalla("confirmacion");
      } else {
        setJust((j) => ({ ...j, archivo_url: url }));
        setCamara(null);
        setPantalla("justificar");
        toast.success("Foto del comprobante lista");
      }
    } catch (e) {
      toast.error(fmtApiError(e));
      setCamara(null);
      if (empleado) { setPantalla("acciones"); refrescarHoy(empleado.id); }
    } finally {
      setProcesando(false);
    }
  };

  const ficharPausa = async (tipo, label) => {
    setProcesando(true);
    try {
      const { data } = await api.post("/kiosco/fichar", { empleado_id: empleado.id, tipo });
      toast.success(`${label} registrada ${data.hora} — se cierra sola a las ${data.cierra}`);
      await refrescarHoy(empleado.id);
    } catch (e) {
      toast.error(fmtApiError(e));
    } finally {
      setProcesando(false);
    }
  };

  const abrirQr = async () => {
    setProcesando(true);
    try {
      const { data } = await api.post("/kiosco/qr-sesion", { empleado_id: empleado.id });
      setQr({ token: data.token, url: `${window.location.origin}/subir/${data.token}` });
      setPantalla("qr");
    } catch (e) {
      toast.error(fmtApiError(e));
    } finally {
      setProcesando(false);
    }
  };

  const enviarJustificativo = async () => {
    setProcesando(true);
    try {
      await api.post("/kiosco/justificativo", { empleado_id: empleado.id, ...just });
      setConfirm({ titulo: "Justificativo enviado", detalle: "Queda pendiente de revisión de RRHH" });
      setPantalla("confirmacion");
    } catch (e) {
      toast.error(fmtApiError(e));
    } finally {
      setProcesando(false);
    }
  };

  const salir = async () => { await logout(); navigate("/login"); };

  // ---- estado de las 5 tarjetas y chip de estado ----
  let pausaAbierta = null;
  if (hoy?.tiene_entrada && !hoy?.tiene_salida) {
    pausaAbierta = hoy.pausas.find((p) => {
      const [h, m] = p.hora.split(":").map(Number);
      const cierre = new Date(ahora);
      cierre.setHours(h, m + MIN_PAUSA[p.tipo], 0, 0);
      return ahora < cierre;
    }) || null;
  }

  const estadoTarjeta = (tipo) => {
    if (!hoy) return "bloqueada";
    if (tipo === "entrada") {
      if (!hoy.tiene_entrada) return { estado: "sugerida" };
      return { estado: "usada", hora: hoy.hora_entrada };
    }
    if (tipo === "salida") {
      if (hoy.tiene_salida) return { estado: "usada", hora: hoy.hora_salida };
      return hoy.tiene_entrada ? { estado: "sugerida" } : { estado: "bloqueada" };
    }
    const tomada = hoy.pausas.find((p) => p.tipo === tipo);
    if (tomada) return { estado: "usada", hora: tomada.hora };
    return hoy.tiene_entrada && !hoy.tiene_salida ? { estado: "disponible" } : { estado: "bloqueada" };
  };

  const chipEstado = !hoy?.tiene_entrada
    ? { texto: "Sin fichar", cls: "bg-white/10 text-gray-300" }
    : hoy.tiene_salida
      ? { texto: "Jornada cerrada", cls: "bg-white/10 text-gray-300" }
      : pausaAbierta
        ? { texto: `En pausa: ${pausaAbierta.tipo}`, cls: "bg-amber-400/15 text-amber-300" }
        : { texto: "En jornada", cls: "bg-[#2DD4BF]/15 text-[#2DD4BF]" };

  return (
    <div className="flex min-h-screen flex-col bg-[#0B0F14] text-white" style={{ fontFamily: "'Inter', sans-serif" }} data-testid="pantalla-kiosco">
      <button
        onClick={salir}
        title="Cerrar sesión del local"
        className="fixed right-4 top-4 z-30 rounded-xl border border-white/10 bg-[#151A21]/80 p-2 text-gray-500 transition-colors hover:border-red-400/50 hover:text-red-300"
        data-testid="kiosco-salir"
      >
        <LogOut size={15} />
      </button>

      <AnimatePresence mode="wait">
        {/* ---------- REPOSO / LOGIN DE EMPLEADO ---------- */}
        {pantalla === "reposo" && (
          <motion.main
            key="reposo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 items-center justify-center gap-14 px-10 xl:gap-24"
            data-testid="kiosco-reposo"
          >
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#2DD4BF] bg-[#2DD4BF]/10 px-4 py-1.5 text-sm font-semibold text-[#2DD4BF]" data-testid="kiosco-local">
                <Store size={15} /> {user?.local_nombre || "—"}
              </span>
              <h1 className="mt-7 text-7xl font-bold tracking-tight text-white">Fichaje</h1>
              <p className="mt-2 text-xl text-gray-400">Tu jornada empieza acá.</p>
              <div className="mt-9 flex items-baseline gap-3" data-testid="kiosco-reloj">
                <span className="text-[100px] font-bold leading-none tracking-tight text-white tabular-nums">{hhmm(ahora)}</span>
                <span className="text-4xl font-medium text-gray-500 tabular-nums">{ss(ahora)}</span>
              </div>
              <p className="mt-4 text-lg text-gray-300" data-testid="kiosco-fecha">{fechaLarga(ahora)}</p>
              <p className="mt-1 text-sm text-gray-500">Hora de Argentina</p>
            </div>

            <div className="w-[400px] shrink-0 rounded-3xl border border-white/10 bg-[#151A21] p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#2DD4BF] text-black">
                  <Fingerprint size={22} />
                </div>
                <div>
                  <p className="font-bold text-white">Ingresá con tu legajo y tu PIN</p>
                  <p className="text-sm text-gray-400">Primero el legajo, después el PIN.</p>
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                <Campo
                  etiqueta="Legajo"
                  valor={etapa === "legajo" ? valor : legajo}
                  activo={etapa === "legajo"}
                  onClick={() => { if (etapa !== "legajo") { setEtapa("legajo"); setValor(legajo); setError(""); } }}
                  testid="campo-legajo"
                />
                <Campo
                  etiqueta="PIN"
                  valor={etapa === "pin" ? valor : ""}
                  puntos
                  activo={etapa === "pin"}
                  onClick={() => { if (legajo.length === 4 && etapa !== "pin") { setEtapa("pin"); setValor(""); setError(""); } }}
                  testid="campo-pin"
                />
              </div>

              {error && (
                <p className="mt-3 rounded-xl border border-red-400/40 bg-red-400/10 px-3 py-2 text-center text-xs font-medium text-red-300" data-testid="error-kiosco">
                  {error}
                </p>
              )}

              <div className="mt-4">
                <Teclado onTecla={tecla} onBorrar={borrar} onEnter={enterReposo} />
              </div>

              <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                <span>También podés usar el teclado de la PC.</span>
                <button
                  type="button"
                  onClick={() => setCamara({ modo: "prueba" })}
                  className="font-medium text-[#2DD4BF] hover:underline"
                  data-testid="probar-camara"
                >
                  Probar la cámara
                </button>
              </div>
            </div>
          </motion.main>
        )}

        {/* ---------- MI JORNADA ---------- */}
        {pantalla === "acciones" && empleado && hoy && (
          <motion.main
            key="acciones"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 justify-center"
            data-testid="kiosco-acciones"
          >
            <div className="w-full max-w-4xl px-8 py-6">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">
                    Local {user?.local_nombre}
                  </p>
                  <h1 className="mt-1 text-3xl font-bold tracking-tight text-white">Mi jornada</h1>
                </div>
                <p className="text-sm text-gray-400">{fechaLarga(ahora)} · {hhmm(ahora)}</p>
              </div>

              <div className="mt-5 flex items-center gap-4 rounded-2xl border border-white/10 bg-[#151A21] p-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#2DD4BF]/15 text-base font-bold text-[#2DD4BF]">
                  {empleado.nombre[0]}{empleado.apellido[0]}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-lg font-bold text-white" data-testid="nombre-empleado">
                    {empleado.nombre} {empleado.apellido}
                  </p>
                  <p className="text-sm text-gray-400">Legajo {empleado.legajo}</p>
                </div>
                <span className={`ml-2 rounded-full px-3 py-1 text-xs font-semibold ${chipEstado.cls}`} data-testid="chip-estado">
                  {chipEstado.texto}
                </span>
                {hoy.entrada_otro_local && (
                  <span className="rounded-full border border-[#2DD4BF]/40 px-3 py-1 text-xs font-medium text-[#2DD4BF]" data-testid="aviso-otro-local">
                    Entrada registrada hoy en Local {hoy.entrada_otro_local}
                  </span>
                )}
                <div className="ml-auto flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => setPantalla("justificar")}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0B0F14] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#2DD4BF]/50"
                    data-testid="btn-justificar"
                  >
                    <FilePlus2 size={16} className="text-[#2DD4BF]" /> Justificar un día
                  </button>
                  <button
                    type="button"
                    onClick={volverReposo}
                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-[#0B0F14] px-4 py-2.5 text-sm font-semibold text-gray-300 transition-colors hover:border-red-400/50 hover:text-red-300"
                    data-testid="btn-cerrar-sesion"
                  >
                    <Power size={16} /> Salir
                  </button>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#151A21] px-4 py-3">
                <CalendarDays size={18} className="shrink-0 text-[#2DD4BF]" />
                <p className="text-sm font-semibold text-white">Horario de hoy · 10:00 a 19:00</p>
                <p className="ml-auto text-xs text-gray-500">50 min de pausas · Desayuno 10 · Almuerzo 30 · Merienda 10</p>
              </div>

              <div className="mt-6">
                <h2 className="text-lg font-bold text-white">Registrar fichada</h2>
                <p className="text-sm text-gray-500">Elegí una acción</p>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <Tarjeta
                  icon={LogIn} titulo="Entrada" subtitulo="Comenzar mi jornada"
                  {...(() => { const t = estadoTarjeta("entrada"); return { estado: t.estado, horaUsada: t.hora }; })()}
                  onClick={() => clickAccion("entrada")} testid="btn-entrada"
                />
                <Tarjeta
                  icon={LogOut} titulo="Salida" subtitulo="Finalizar mi jornada"
                  {...(() => { const t = estadoTarjeta("salida"); return { estado: t.estado, horaUsada: t.hora }; })()}
                  onClick={() => clickAccion("salida")} testid="btn-salida"
                />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {PAUSAS.map(({ tipo, label, sub, icon }) => {
                  const t = estadoTarjeta(tipo);
                  return (
                    <Tarjeta
                      key={tipo}
                      icon={icon} titulo={label} subtitulo={sub} minutos={MIN_PAUSA[tipo]}
                      estado={t.estado} horaUsada={t.hora}
                      onClick={() => ficharPausa(tipo, label)} testid={`btn-${tipo}`}
                    />
                  );
                })}
              </div>

              <div className="mt-6 space-y-1 text-center">
                <p className="text-xs text-gray-500">La entrada y la salida piden tu PIN y una foto; las pausas se registran con un toque y terminan solas.</p>
                <p className="flex items-center justify-center gap-1.5 text-xs text-gray-600">
                  <Clock3 size={12} /> La sesión se cierra sola después de un minuto sin uso.
                </p>
              </div>
            </div>
          </motion.main>
        )}

        {/* ---------- RE-PIN ---------- */}
        {pantalla === "repin" && empleado && (
          <motion.main
            key="repin"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 flex-col items-center justify-center px-6"
            data-testid="kiosco-repin"
          >
            <div className="w-[340px] rounded-3xl border border-white/10 bg-[#151A21] p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#2DD4BF] text-black">
                  <Fingerprint size={22} />
                </div>
                <div>
                  <p className="font-bold text-white">Confirmá tu PIN</p>
                  <p className="text-sm text-gray-400">
                    {accion === "entrada" ? "Entrada" : "Salida"} — {empleado.nombre} {empleado.apellido}
                  </p>
                </div>
              </div>
              <div className="mt-5">
                <Campo etiqueta="PIN" valor={valor} puntos activo onClick={() => {}} testid="campo-repin" />
              </div>
              {error && (
                <p className="mt-3 rounded-xl border border-red-400/40 bg-red-400/10 px-3 py-2 text-center text-xs font-medium text-red-300" data-testid="error-repin">
                  {error}
                </p>
              )}
              <div className="mt-4">
                <Teclado onTecla={tecla} onBorrar={borrar} onEnter={enterRepin} />
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPantalla("acciones")}
              className="mt-4 text-xs text-gray-500 underline-offset-2 hover:text-white hover:underline"
              data-testid="repin-volver"
            >
              ← Volver
            </button>
          </motion.main>
        )}

        {/* ---------- JUSTIFICATIVO ---------- */}
        {pantalla === "justificar" && empleado && (
          <motion.main
            key="justificar"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-4"
            data-testid="kiosco-justificar"
          >
            <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#151A21] p-6">
              <h2 className="text-xl font-bold text-white">Justificar un día</h2>
              <p className="mb-4 mt-0.5 text-sm text-gray-400">{empleado.nombre} {empleado.apellido} — queda pendiente de revisión de RRHH</p>

              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Tipo</p>
              <div className="mb-4 grid grid-cols-3 gap-2" data-testid="just-tipos">
                {TIPOS_JUST.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setJust((j) => ({ ...j, tipo: t.id }))}
                    className={`rounded-xl border px-2 py-2.5 text-xs font-semibold transition-all active:scale-95 ${
                      just.tipo === t.id ? "border-[#2DD4BF] bg-[#2DD4BF]/15 text-[#2DD4BF]" : "border-white/10 bg-[#0B0F14] text-gray-400 hover:text-white"
                    }`}
                    data-testid={`just-tipo-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="mb-4 grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="just-fecha" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Fecha</label>
                  <input
                    id="just-fecha"
                    type="date"
                    value={just.fecha}
                    onChange={(e) => setJust((j) => ({ ...j, fecha: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-[#0B0F14] px-3 py-2.5 text-sm text-white outline-none focus:border-[#2DD4BF] [color-scheme:dark]"
                    data-testid="just-fecha"
                  />
                </div>
                <div>
                  <label htmlFor="just-desc" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">Descripción corta</label>
                  <input
                    id="just-desc"
                    value={just.descripcion}
                    onChange={(e) => setJust((j) => ({ ...j, descripcion: e.target.value }))}
                    placeholder="ej. turno médico"
                    className="w-full rounded-xl border border-white/10 bg-[#0B0F14] px-3 py-2.5 text-sm text-white outline-none placeholder:text-gray-600 focus:border-[#2DD4BF]"
                    data-testid="just-descripcion"
                  />
                </div>
              </div>

              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Comprobante</p>
              {just.archivo_url ? (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 px-4 py-3" data-testid="just-comprobante-ok">
                  <CheckCircle2 size={22} className="shrink-0 text-[#2DD4BF]" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Comprobante listo</p>
                    <button type="button" onClick={() => setJust((j) => ({ ...j, archivo_url: null }))} className="text-xs text-gray-400 underline-offset-2 hover:underline" data-testid="just-cambiar-comprobante">
                      cambiar
                    </button>
                  </div>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${just.archivo_url}`} alt="Comprobante" className="h-12 w-12 rounded-lg border border-white/10 object-cover" />
                </div>
              ) : (
                <div className="mb-4 grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCamara({ modo: "comprobante" })}
                    className="flex h-16 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#0B0F14] text-sm font-semibold text-white transition-all hover:border-[#2DD4BF]/50 active:scale-[0.98]"
                    data-testid="just-foto"
                  >
                    <Paperclip size={18} className="text-[#2DD4BF]" /> Sacar foto al papel
                  </button>
                  <button
                    type="button"
                    onClick={abrirQr}
                    className="flex h-16 items-center justify-center gap-2 rounded-2xl border border-white/10 bg-[#0B0F14] text-sm font-semibold text-white transition-all hover:border-[#2DD4BF]/50 active:scale-[0.98]"
                    data-testid="just-qr"
                  >
                    <QrCode size={18} className="text-[#2DD4BF]" /> Subir desde el celular
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPantalla("acciones")}
                  className="rounded-2xl border border-white/10 py-3.5 text-sm font-semibold text-gray-400 transition-colors hover:text-white"
                  data-testid="just-cancelar"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={enviarJustificativo}
                  disabled={procesando || !just.archivo_url || just.descripcion.trim().length < 3}
                  className="rounded-2xl bg-[#2DD4BF] py-3.5 text-sm font-bold text-black transition-all hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="just-enviar"
                >
                  Enviar a RRHH
                </button>
              </div>
            </div>
          </motion.main>
        )}

        {/* ---------- QR ---------- */}
        {pantalla === "qr" && qr && (
          <motion.main
            key="qr"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 items-center justify-center px-6"
            data-testid="kiosco-qr"
          >
            <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#151A21] p-6 text-center">
              <h2 className="text-xl font-bold text-white">Subilo desde tu celular</h2>
              <p className="mb-4 mt-1 text-sm text-gray-400">Escaneá el código y adjuntá la foto o el PDF. Vence en 10 minutos.</p>
              <div className="mx-auto mb-4 w-fit rounded-2xl bg-white p-3">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/kiosco/qr/${qr.token}.svg?url=${encodeURIComponent(qr.url)}`}
                  alt="Código QR para subir el comprobante"
                  className="h-52 w-52"
                  data-testid="qr-imagen"
                />
              </div>
              <p className="flex items-center justify-center gap-2 text-sm text-gray-400" data-testid="qr-esperando">
                <Loader2 size={15} className="animate-spin text-[#2DD4BF]" /> Esperando el archivo…
              </p>
              <button
                type="button"
                onClick={() => { setQr(null); setPantalla("justificar"); }}
                className="mt-4 text-xs text-gray-500 underline-offset-2 hover:text-white hover:underline"
                data-testid="qr-cancelar"
              >
                ← Volver
              </button>
            </div>
          </motion.main>
        )}

        {/* ---------- CONFIRMACIÓN ---------- */}
        {pantalla === "confirmacion" && confirm && (
          <motion.main
            key="confirmacion"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-1 items-center justify-center px-6"
            data-testid="confirmacion-fichada"
          >
            <div className="w-full max-w-md rounded-3xl border border-[#2DD4BF]/40 bg-[#151A21] px-10 py-10 text-center">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#2DD4BF]/15"
              >
                <CheckCircle2 size={44} className="text-[#2DD4BF]" strokeWidth={1.8} />
              </motion.div>
              <h2 className="mt-5 text-3xl font-bold tracking-tight text-white" data-testid="confirmacion-titulo">{confirm.titulo}</h2>
              <p className="mt-2 text-lg text-gray-400" data-testid="confirmacion-detalle">{confirm.detalle}</p>
              <p className="mt-7 text-xs text-gray-500">Volviendo al inicio en 3 s…</p>
            </div>
          </motion.main>
        )}
      </AnimatePresence>

      {camara && (
        <Camara
          titulo={camara.modo === "prueba" ? "Prueba de cámara" : camara.modo === "fichada" ? `Foto para ${accion === "entrada" ? "la entrada" : "la salida"}` : "Foto del comprobante"}
          soloPrueba={camara.modo === "prueba"}
          onCaptura={onFoto}
          onCancelar={() => { setCamara(null); if (empleado) setPantalla(camara.modo === "comprobante" ? "justificar" : "acciones"); }}
        />
      )}

      {procesando && !camara && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#0B0F14]/60" data-testid="procesando">
          <Loader2 size={40} className="animate-spin text-[#2DD4BF]" />
        </div>
      )}

      {empleado && restante <= 10 && restante > 0 && (
        <div className="fixed bottom-5 left-1/2 z-40 -translate-x-1/2 rounded-full bg-red-500 px-5 py-2 text-sm font-bold text-white shadow-lg" data-testid="aviso-inactividad">
          La sesión se cierra en {restante} s — tocá la pantalla para seguir
        </div>
      )}
    </div>
  );
}
